import { useState } from 'react';

interface ArcanaLogoProps {
  variant?: 'full' | 'icon' | 'compact';
  className?: string;
}

// Fallback SVG component
function LogoIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 40 40"
      className={className}
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      {/* Deep teal circle background */}
      <circle cx="20" cy="20" r="18" fill="oklch(0.45 0.12 195)" />
      {/* Interwoven infinity-inspired symbol */}
      <path
        d="M12 20C12 16.5 15 14 20 14C25 14 28 16.5 28 20C28 23.5 25 26 20 26C15 26 12 23.5 12 20Z"
        stroke="oklch(0.85 0.08 195)"
        strokeWidth="2.5"
        strokeLinecap="round"
      />
      <path
        d="M12 20C12 23.5 15 26 20 26"
        stroke="oklch(0.85 0.08 195)"
        strokeWidth="2.5"
        strokeLinecap="round"
      />
    </svg>
  );
}

export function ArcanaLogo({ variant = 'full', className = '' }: ArcanaLogoProps) {
  const [useFallback, setUseFallback] = useState(false);

  // Image sizes for each variant
  const sizes = {
    icon: 'w-8 h-8',
    compact: 'w-10 h-10',
    full: 'w-12 h-12',
  };

  if (variant === 'icon') {
    if (useFallback) {
      return <LogoIcon className={className} />;
    }
    return (
      <img
        src="/almic-logo.png"
        alt="Arcana CMS"
        className={className || sizes.icon}
        onError={() => setUseFallback(true)}
      />
    );
  }

  if (variant === 'compact') {
    return (
      <div className={`flex items-center gap-2 ${className}`}>
        {useFallback ? (
          <LogoIcon className="w-10 h-10" />
        ) : (
          <img
            src="/almic-logo.png"
            alt="Arcana CMS"
            className={sizes.compact}
            onError={() => setUseFallback(true)}
          />
        )}
        <div className="flex flex-col">
          <span className="font-display font-bold text-lg tracking-tight">Arcana CMS</span>
          <small className="text-xs text-muted-foreground">by Almic Digital</small>
        </div>
      </div>
    );
  }

  // Full logo with icon and text
  return (
    <div className={`flex items-center gap-3 ${className}`}>
      {useFallback ? (
        <LogoIcon className="w-12 h-12" />
      ) : (
        <img
          src="/almic-logo.png"
          alt="Arcana CMS"
          className={sizes.full}
          onError={() => setUseFallback(true)}
        />
      )}
      <div className="flex flex-col">
        <span className="font-display font-bold text-xl tracking-tight leading-tight">Arcana CMS</span>
        <span className="text-xs text-muted-foreground font-medium">by Almic Digital</span>
      </div>
    </div>
  );
}
